import React from "react"
import { Redirect, Route } from "react-router-dom"
import { AppContainer } from "#/components/containers/app/AppContainer"
import LoginContainer from "#/components/containers/login/LoginContainer"
import "#/css/transitions.css"
import { FadeTransition } from "./molecules/Molecules"
import ResetPasswordContainer from "../containers/login/ResetPasswordContainer"
import RequestResetPasswordContainer from "../containers/login/RequestResetPasswordContainer"
import ResetPasswordSuccess from "../../pages/reset-password-success"
import ResetPasswordError from "../../pages/reset-password-error"
import MerchantDeclineContainer from "../containers/merchantdecline/MerchantDeclineContainer"
import SignUp from "#/pages/signup"
import SignUpSuccess from "#/pages/signup-success"
import SignUpFindAccount from "#/pages/signup-find-account"
import SignUpFindAccountSuccess from "#/pages/signup-find-account-success"
import FindAccountByExternalEmployeeIdSuccess from "#/pages/signup-find-account-by-external-employee-id-success"
import SignUpGetHelp from "../../pages/signup-get-help"
import SignUpGetHelpSuccess from "../../pages/signup-get-help-success"
import SignUpEmployeeAlreadyRegistered from "../../pages/signup-employee-already-registered"
import SignUpEmployeeHasSaml from "../../pages/signup-employee-has-saml"
import SignUpInfo from "#/pages/signup/info"
import SignUpShareZipCode from "#/pages/signup-share-zip-code"
import { useFlags, withLDProvider } from "launchdarkly-react-client-sdk"
import { basicLogger } from "launchdarkly-js-client-sdk"
import { useDispatch } from "react-redux"
import { carrotClient } from "#/utils/CarrotClient"
import { signIn } from "#/redux/actions/loginActions"
import Settings from "../../utils/CarrotConfig"
import { BiometricAuthEnrollmentContextProvider } from "../context/BiometricAuthEnrollment/BiometricAuthEnrollmentContext"
import PartnerRegistrationFlow from "../partner-access/partner-registration/PartnerRegistrationFlow"
import { LoadingInner } from "./Loading"
import SamlNoEmailOnboardingCaptureEmail from "../../pages/saml-no-email-onboarding-capture-email"
import { Switch } from "#/services/routing/Switch"
import { useLocaleContext } from "#/components/context/user/LocaleContext"
import { CarrotRxExtend } from "#/pages/carrot-rx-extend"
import { createErrorBoundary } from "#/utils/createErrorBoundary"
import { AppAccessError } from "#/components/views/AppAccessError"
import MultiFactorAuthContainer from "#/components/containers/login/MultiFactorAuthContainer"
import { RegistrationFlow } from "#/components/views/register/RegistrationFlow"
// TEMPORARY (CONAPP-155): the static distribution landing page mounted at `/consumer` below.
// Imported eagerly rather than lazily like the package surfaces — it's a thin iframe wrapper, and the
// page itself is a `public/` document the browser fetches, so there's nothing worth code-splitting.
import ConsumerLandingPage from "#/pages/consumer-landing/ConsumerLandingPage"

// The Consumer package surfaces. Both are mounted below, ABOVE the authenticated / unauthenticated
// split, so they render for signed-out members too (for now `/consumer` is public as well, not just
// `/consumer/subscription`). Code-split and lazy-loaded, so none of the package's code loads until
// someone navigates to a `/consumer` path.
const ConsumerRouter = React.lazy(() => import("@internal/consumer").then((m) => ({ default: m.ConsumerRouter })))
const ConsumerSubscriptionRoute = React.lazy(() =>
  import("@internal/consumer").then((m) => ({ default: m.ConsumerSubscriptionRoute }))
)

const Root = ({
  loggedIn,
  loadingIsLoggedIn,
  email,
  memberProfile
}: {
  loggedIn: boolean
  loadingIsLoggedIn: boolean
  email?: string
  // The signed-in member's carrot-app profile fields (redux `userInfo`), forwarded
  // to the consumer subscription funnel to prefill the details step. Carrot-app-native
  // values (`state` = 2-letter abbreviation, `dateOfBirth` = "YYYY-MM-DD").
  memberProfile?: {
    firstName?: string
    lastName?: string
    dateOfBirth?: string
    state?: string
    zip?: string
  }
}) => {
  const { didCompiledMessagesLoad } = useLocaleContext()
  // Single gate for ALL consumer-app work (CAP-135): the `consumer-app-sign-up`
  // LaunchDarkly flag both mounts the `/consumer` routes below AND enables the
  // subscription funnel's net-new account provisioning (vs. its stub). Off ⇒ the
  // consumer app is entirely dark.
  const { consumerAppSignUp } = useFlags()
  const dispatch = useDispatch()
  // Net-new self-serve signup (CAP-135): after the account step provisions the
  // member, establish a Carrot session so the PROTECTED checkout is reachable. Built
  // in the host (needs carrotClient + redux) and passed into the consumer package via
  // its host contract. Mirrors the SPA's proven prelogin -> auth/login -> signIn()
  // sequence (a consumer self-serve account is always password-auth, so prelogin's
  // SSO branch doesn't apply); an MFA-required response or any failure throws so the
  // account step surfaces it (and stays put) instead of dead-ending at checkout.
  const handleConsumerLogin = React.useCallback(
    async (loginEmail: string, password: string): Promise<void> => {
      await carrotClient.prelogin(loginEmail)
      const response = await carrotClient.login(loginEmail, password)
      if (response?.message) {
        throw new Error(`consumer login incomplete: ${response.message}`)
      }
      await (dispatch as unknown as (thunk: unknown) => Promise<void>)(signIn())
    },
    [dispatch]
  )
  const showPage = didCompiledMessagesLoad && !loadingIsLoggedIn

  return (
    <div>
      {showPage ? (
        <Switch allowedRouters={[NonAuthenticatedRoutesContainer]}>
          <Route path="/register/partner/:guid" component={PartnerRegistrationFlow} />
          <Route path="/register/:guid" component={RegistrationFlow} />
          <Route path="/saml-no-email-onboarding" component={SamlNoEmailOnboardingCaptureEmail} />
          <Route path={"/carrot-rx-extend"} component={CarrotRxExtend} />
          {/* Consumer routes — gated behind the `consumer-app-sign-up` flag, so the whole consumer app
              is dark until it's enabled. Declared above the auth split so they resolve for signed-out
              members before the app/login branch. `/consumer/subscription` is a PREFIX route (no
              `exact`): the package's nested SubscriptionRouter owns the funnel steps beneath it, and it
              MUST stay above the non-exact `/consumer` route so it isn't swallowed. Each is a separate
              gated child (not wrapped in a Fragment) so the custom Switch's child-type check passes. */}
          {/* Authenticated subscription-billing page — hoisted ABOVE the public `/consumer`
              routes so a signed-in member gets the billing page inside the authed chrome
              (nav/sidebar/footer via AppContainer) instead of the public consumer app. When
              `consumerAppSignUp` is OFF this branch isn't mounted, but the bottom
              `loggedIn ? AppContainer` already handles this path (the public `/consumer` prefix
              route is likewise not mounted, so there's no collision). When it is ON, this
              exact-match authed route MUST precede the non-exact `/consumer` prefix route below
              so the prefix route can't swallow it.

              Unlike the rest of `/consumer`, this page is NOT public: it renders a member's own
              billing details. So the route is mounted for signed-out visitors too and mirrors
              the auth split at the bottom of this Switch — signed out renders the login page at
              this URL instead of falling through to the public consumer app (whose router would
              bounce the unknown subpath to `/consumer`). Because the URL is preserved, the
              billing page renders as soon as `loggedIn` flips after a successful sign-in.
              Kept as a discrete `{cond && <Route/>}` child (not wrapped in a Fragment) to
              satisfy the custom Switch's child-type rules. */}
          {consumerAppSignUp && (
            <Route
              exact
              path="/consumer/subscription-billing"
              render={() => (loggedIn ? <AppContainer key="app" /> : <NonAuthenticatedRoutesContainer />)}
            />
          )}
          {consumerAppSignUp && (
            <Route
              path="/consumer/subscription"
              render={() => (
                <React.Suspense fallback={<LoadingInner />}>
                  <ConsumerSubscriptionRoute
                    isLoggedIn={loggedIn}
                    email={email}
                    profile={memberProfile}
                    stripePublishableKey={Settings.STRIPE_PUBLISHABLE_KEY}
                    selfServeSignupEnabled={consumerAppSignUp}
                    onLogin={handleConsumerLogin}
                  />
                </React.Suspense>
              )}
            />
          )}
          {/* TEMPORARY (CONAPP-155) — static distribution landing page. Shadows the consumer app's own
              `/consumer` home (`ConsumerRouter` → `ConsumerHome`) until the temporary page is pulled;
              every other `/consumer/*` path still resolves through `ConsumerRouter` below, whose
              unknown-path redirect back to `/consumer` now lands here. `exact` so it takes only
              `/consumer` itself, and it MUST stay above the non-exact `/consumer` prefix route below or
              that route swallows it. Gated on the same `consumerAppSignUp` flag as the rest of the
              consumer app, and kept as a discrete `{cond && <Route/>}` child for the custom Switch's
              child-type check. Removal: delete this block, the import above,
              `src/pages/consumer-landing/`, and `public/consumer-landing.html`. */}
          {consumerAppSignUp && <Route exact path="/consumer" component={ConsumerLandingPage} />}
          {consumerAppSignUp && (
            <Route
              path="/consumer"
              render={() => (
                <React.Suspense fallback={<LoadingInner />}>
                  <ConsumerRouter isLoggedIn={loggedIn} />
                </React.Suspense>
              )}
            />
          )}
          {loggedIn ? <Route render={() => <AppContainer key="app" />} /> : <NonAuthenticatedRoutesContainer />}
        </Switch>
      ) : (
        <LoadingInner />
      )}
    </div>
  )
}

const NonAuthenticatedRoutesErrorBoundary = createErrorBoundary(AppAccessError, "NonAuthenticatedRoutesContainer")

const NonAuthenticatedRoutesContainer = () => {
  return (
    <NonAuthenticatedRoutesErrorBoundary>
      <Switch>
        <Route path="/signup/info/:parentCompanyId" component={SignUpInfo} />
        <Route path="/signup/info" component={SignUpInfo} />
        <Route
          path="/signup"
          render={() => (
            <FadeTransition>
              <SignUp />
            </FadeTransition>
          )}
        />

        <Route path="/signup-success" component={SignUpSuccess} />
        <Route path="/signup-find-account" component={SignUpFindAccount} />
        <Route path="/signup-find-account-success" component={SignUpFindAccountSuccess} />
        <Route
          path="/signup-find-account-by-external-employee-id-success"
          component={FindAccountByExternalEmployeeIdSuccess}
        />
        <Route path="/signup-get-help" component={SignUpGetHelp} />
        <Route path="/signup-get-help-success" component={SignUpGetHelpSuccess} />
        <Route path="/signup-employee-already-registered" component={SignUpEmployeeAlreadyRegistered} />
        <Route path="/signup-employee-has-saml" component={SignUpEmployeeHasSaml} />
        <Route path="/signup-share-zip-code" component={SignUpShareZipCode} />
        <Route path="/reset-password-success" component={ResetPasswordSuccess} />
        <Route path="/reset-password-error" component={ResetPasswordError} />
        <Route exact path="/reset-password" component={ResetPasswordContainer} />
        <Route path="/request-reset-password" component={RequestResetPasswordContainer} />
        <Route path="/confirm/:token" component={MerchantDeclineContainer} />
        <Route path="/login/enter-code" component={MultiFactorAuthContainer} />

        <Redirect exact path="/sign-up" to="/signup" />
        {/* This needs to stay on the bottom since it doesn't have a path and will override any routes declared below it. */}
        <Route
          render={() => (
            <FadeTransition>
              <BiometricAuthEnrollmentContextProvider>
                <LoginContainer key="login" />
              </BiometricAuthEnrollmentContextProvider>
            </FadeTransition>
          )}
        />
      </Switch>
    </NonAuthenticatedRoutesErrorBoundary>
  )
}

export default withLDProvider({
  clientSideID: Settings.LAUNCH_DARKLY_CLIENT_ID,
  user: {
    key: "anon",
    email: "anon@get-carrot.com"
  },
  timeout: 3,
  options: {
    logger: basicLogger({ level: "warn" })
  }
})(Root)
